import type { KeyboardEvent, ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { Avatar } from '../../Common/Avatar'
import { Pressable } from '../../Common/Pressable'
import type { TeamPersonRow, PersonStats, ViewerTier } from '../../../utils/overviewInsights'

export interface PeopleSectionProps {
  tier: ViewerTier
  people: TeamPersonRow[]
  you: PersonStats | null
  teamTotals: { open: number; done7d: number; overdue: number }
  userDisplayName?: string
  userRole?: string
  userSeries7d?: number[]
  helperReliabilityMap?: Record<string, number>
  onSelectPerson?: (person: TeamPersonRow) => void
  onNavigateToAnalysis?: (tab: 'TEAM' | 'WORK') => void
  toolsNode?: ReactNode
}

export const Sparkline = ({
  values,
  width = 120,
  height = 34,
}: {
  values: number[]
  width?: number
  height?: number
}) => {
  const safeValues = values && values.length > 0 ? values : [0, 0, 0, 0, 0, 0, 0]
  const max = Math.max(1, ...safeValues)
  const len = Math.max(2, safeValues.length)
  const pts = safeValues
    .map(
      (x, i) =>
        `${((i / (len - 1)) * width).toFixed(1)},${(height - 3 - (x / max) * (height - 6)).toFixed(1)}`,
    )
    .join(' ')

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline
        points={pts}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

export const StackedBar = ({
  done,
  open,
  overdue,
}: {
  done: number
  open: number
  overdue: number
}) => {
  const total = Math.max(1, done + open)
  const donePct = (done / total) * 100
  const openNormalPct = (Math.max(0, open - overdue) / total) * 100
  const overduePct = (overdue / total) * 100

  return (
    <div className="overviewStack" aria-hidden="true">
      <i style={{ width: `${donePct}%`, background: 'var(--success)' }} />
      <i style={{ width: `${openNormalPct}%`, background: 'var(--accent)' }} />
      <i style={{ width: `${overduePct}%`, background: 'var(--danger)' }} />
    </div>
  )
}

export const PeopleSection = ({
  tier,
  people,
  you,
  teamTotals,
  userDisplayName,
  userRole,
  userSeries7d,
  helperReliabilityMap = {},
  onSelectPerson,
  onNavigateToAnalysis,
  toolsNode,
}: PeopleSectionProps) => {
  const { t } = useNls()

  if (tier === 'helper') {
    return null
  }

  if (tier === 'lead') {
    return (
      <div className="overviewCard" data-testid="overview-people-section">
        {toolsNode}
        <div className="overviewCard__header">
          <div>
            <h3 className="overviewCard__title">
              {t('NLSOverviewPeopleTitle')}
              <span className="overviewPill overviewPill--neutral">{people.length}</span>
            </h3>
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('NLSOverviewPeopleSubtitle')}
            </span>
          </div>
          {onNavigateToAnalysis && (
            <Pressable
              type="button"
              className="overviewCard__link"
              onClick={() => onNavigateToAnalysis('TEAM')}
            >
              {t('NLSOverviewTeamAnalysisLink')}
            </Pressable>
          )}
        </div>

        <div className="overviewPgrid">
          {people.map((person) => {
            const rel = helperReliabilityMap[person.uid]
            const flagText =
              person.flag === 'overloaded'
                ? t('NLSOverviewFlagOverloaded')
                : person.flag === 'quiet'
                  ? t('NLSOverviewFlagQuiet')
                  : null

            const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelectPerson?.(person)
              }
            }

            return (
              <div
                key={person.uid}
                role="button"
                tabIndex={0}
                className="overviewPersonCard"
                data-person-card={person.uid}
                onClick={() => onSelectPerson?.(person)}
                onKeyDown={handleKeyDown}
                aria-label={`${person.displayName}, ${person.role ?? t('NLSOverviewRoleMember')}`}
              >
                <div className="overviewPersonCard__top">
                  <Avatar name={person.displayName} size="sm" />
                  <div className="overviewPersonCard__info">
                    <div className="overviewPersonCard__name">{person.displayName}</div>
                    <div className="overviewPersonCard__meta">
                      {person.role ?? t('NLSOverviewRoleMember')}
                    </div>
                  </div>
                  {flagText && (
                    <span
                      className={`overviewPill ${
                        person.flag === 'overloaded' ? 'overviewPill--bad' : 'overviewPill--warn'
                      }`}
                      style={{ marginLeft: 'auto' }}
                    >
                      {flagText}
                    </span>
                  )}
                </div>

                <StackedBar
                  done={person.done7d}
                  open={person.open}
                  overdue={person.overdue}
                />

                <div className="overviewPersonCard__metrics">
                  <span>
                    <strong>{person.done7d}</strong> {t('NLSOverviewLegendDone')}
                  </span>
                  <span>
                    <strong>{person.open}</strong> {t('NLSOverviewLegendOpen')}
                  </span>
                  <span>
                    <strong>{person.overdue}</strong> {t('NLSOverviewLegendOverdue')}
                  </span>
                  {rel != null && (
                    <span title={t('NLSOverviewLegendReliability')}>
                      ★ <strong>{rel}</strong>
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="overviewLegend">
          <span className="overviewLegendItem">
            <i className="overviewLegendDot" style={{ background: 'var(--success)' }} />
            {t('NLSOverviewLegendDone')}
          </span>
          <span className="overviewLegendItem">
            <i className="overviewLegendDot" style={{ background: 'var(--accent)' }} />
            {t('NLSOverviewLegendOpen')}
          </span>
          <span className="overviewLegendItem">
            <i className="overviewLegendDot" style={{ background: 'var(--danger)' }} />
            {t('NLSOverviewLegendOverdue')}
          </span>
          <span className="overviewLegendItem">
            ★ {t('NLSOverviewLegendReliability')}
          </span>
        </div>
      </div>
    )
  }

  // Viewer / Read-only mode
  const youDone = you?.done7d ?? 0
  const youOpen = you?.open ?? 0
  const youOverdue = you?.overdue ?? 0

  return (
    <div className="overviewCard" data-testid="overview-people-section">
      {toolsNode}
      <div className="overviewCard__header">
        <div>
          <h3 className="overviewCard__title">{t('NLSOverviewPeopleTitle')}</h3>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {t('NLSOverviewViewerNotice')}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        {/* Your week */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-panel)',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--muted)',
            }}
          >
            {t('NLSOverviewViewerYourWeek')}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Avatar name={userDisplayName ?? t('NLSOverviewYouFallback')} size="md" />
            <div>
              <strong style={{ color: 'var(--text-strong)', fontSize: '14px' }}>
                {userDisplayName ?? t('NLSOverviewYouFallback')}
              </strong>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {userRole ?? t('NLSOverviewRoleViewer')}
              </div>
            </div>
          </div>

          <StackedBar done={youDone} open={youOpen} overdue={youOverdue} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              color: 'var(--muted)',
            }}
          >
            <span>
              <strong style={{ color: 'var(--text-strong)' }}>{youDone}</strong>{' '}
              {t('NLSOverviewLegendDone')}
            </span>
            <span>
              <strong style={{ color: 'var(--text-strong)' }}>{youOpen}</strong>{' '}
              {t('NLSOverviewLegendOpen')}
            </span>
            <span>
              <strong style={{ color: 'var(--text-strong)' }}>{youOverdue}</strong>{' '}
              {t('NLSOverviewLegendOverdue')}
            </span>
          </div>

          {userSeries7d && userSeries7d.length > 0 && (
            <div style={{ marginTop: '4px' }}>
              <Sparkline values={userSeries7d} width={240} height={34} />
            </div>
          )}
        </div>

        {/* Team */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-panel)',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--muted)',
            }}
          >
            {t('NLSOverviewViewerTeam')}
          </span>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'baseline' }}>
            <div>
              <strong
                style={{
                  fontSize: '22px',
                  fontWeight: 800,
                  fontFamily: 'var(--font-figures)',
                  color: 'var(--text-strong)',
                }}
              >
                {teamTotals.open}
              </strong>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {t('NLSOverviewLegendOpen')}
              </div>
            </div>
            <div>
              <strong
                style={{
                  fontSize: '22px',
                  fontWeight: 800,
                  fontFamily: 'var(--font-figures)',
                  color: 'var(--text-strong)',
                }}
              >
                {teamTotals.done7d}
              </strong>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {t('NLSOverviewLegendDone')}
              </div>
            </div>
            <div>
              <strong
                style={{
                  fontSize: '22px',
                  fontWeight: 800,
                  fontFamily: 'var(--font-figures)',
                  color: teamTotals.overdue > 0 ? 'var(--danger)' : 'var(--text-strong)',
                }}
              >
                {teamTotals.overdue}
              </strong>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {t('NLSOverviewLegendOverdue')}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {people.map((person) => (
              <span
                key={person.uid}
                className="overviewPill overviewPill--neutral"
                style={{ padding: '3px 8px' }}
              >
                {person.displayName} · {person.role ?? t('NLSOverviewRoleMember')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
